import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  listTemplates,
  createTemplate as apiCreateTemplate,
  updateTemplate as apiUpdateTemplate,
  deleteTemplate as apiDeleteTemplate,
  reorderTemplates as apiReorderTemplates,
  listBackgrounds,
  uploadBackground as apiUploadBackground,
  updateBackground as apiUpdateBackground,
  deleteBackground as apiDeleteBackground,
  backgroundImageUrl,
  listAudioTracks,
  uploadAudioTrack as apiUploadAudioTrack,
  updateAudioTrack as apiUpdateAudioTrack,
  deleteAudioTrack as apiDeleteAudioTrack,
  audioTrackUrl,
  getTemplateUsage,
  recordTemplateUsage as apiRecordTemplateUsage,
  getUserState,
  saveUserState,
} from '../lib/api';
import { builtInTemplates } from '../data/templates';
import { builtInBackgrounds, defaultBackground } from '../data/backgrounds';
import { useAuth } from './AuthContext';

const UserDataContext = createContext(null);

const resolveBackgroundUrl = (bg) => {
  if (!bg) return null;
  if (bg.builtin) return bg.url;
  return backgroundImageUrl(bg.id);
};

export const UserDataProvider = ({ children }) => {
  const { user } = useAuth();
  const [customTemplates, setCustomTemplates] = useState([]);
  const [customBackgrounds, setCustomBackgrounds] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [templateUsage, setTemplateUsage] = useState([]);
  const [savedState, setSavedState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial load on auth
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listTemplates().catch(() => []),
      listBackgrounds().catch(() => []),
      listAudioTracks().catch(() => []),
      getTemplateUsage().catch(() => []),
      getUserState().catch(() => null),
    ]).then(([tpls, bgs, tracks, usage, state]) => {
      if (cancelled) return;
      setCustomTemplates(tpls || []);
      setCustomBackgrounds(bgs || []);
      setAudioTracks(tracks || []);
      setTemplateUsage(usage || []);
      setSavedState(state && Object.keys(state).length ? state : null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ===== Template helpers =====
  const allTemplates = [
    ...Object.values(builtInTemplates),
    ...customTemplates.map((t) => ({ ...t, builtin: false })),
  ];

  const findTemplate = (id) => allTemplates.find((t) => t.id === id);

  const createTemplate = useCallback(async (body) => {
    const newTpl = await apiCreateTemplate(body);
    setCustomTemplates((prev) => [...prev, newTpl]);
    return newTpl;
  }, []);

  const updateTemplate = useCallback(async (id, body) => {
    const updated = await apiUpdateTemplate(id, body);
    setCustomTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  }, []);

  const deleteTemplate = useCallback(async (id) => {
    await apiDeleteTemplate(id);
    setCustomTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Persist a new order for custom templates. The list passed in must be the
  // full ordered array of custom-template ids (built-ins are never reordered).
  const reorderTemplates = useCallback(async (orderedIds) => {
    // Optimistic local reorder so the UI feels instant
    setCustomTemplates((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
      // Append anything not present in orderedIds (defensive)
      for (const t of prev) if (!orderedIds.includes(t.id)) next.push(t);
      return next;
    });
    try {
      await apiReorderTemplates(orderedIds);
    } catch (e) {
      console.warn('Reorder failed, refetching templates', e);
      // Fallback: refetch to reconcile order
      try {
        const tpls = await listTemplates();
        setCustomTemplates(tpls || []);
      } catch (_) { /* non-fatal */ }
    }
  }, []);

  // ===== Background helpers =====
  const allBackgrounds = [
    ...Object.values(builtInBackgrounds),
    ...customBackgrounds.map((b) => ({
      ...b,
      builtin: false,
      url: backgroundImageUrl(b.id),
    })),
  ];

  const findBackground = (id) => allBackgrounds.find((b) => b.id === id);

  const uploadBackground = useCallback(async (file, name, visibility = 'private') => {
    const newBg = await apiUploadBackground(file, name, visibility);
    setCustomBackgrounds((prev) => [...prev, newBg]);
    return newBg;
  }, []);

  const updateBackground = useCallback(async (id, body) => {
    const updated = await apiUpdateBackground(id, body);
    setCustomBackgrounds((prev) => prev.map((b) => (b.id === id ? updated : b)));
    return updated;
  }, []);

  const deleteBackground = useCallback(async (id) => {
    await apiDeleteBackground(id);
    setCustomBackgrounds((prev) => prev.filter((b) => b.id !== id));
  }, []);

  // ===== Audio Tracks =====
  const uploadAudioTrack = useCallback(async (file, name, visibility = 'private', durationSec) => {
    const newTrack = await apiUploadAudioTrack(file, name, visibility, durationSec);
    setAudioTracks((prev) => [...prev, newTrack]);
    return newTrack;
  }, []);

  const updateAudioTrack = useCallback(async (id, body) => {
    const updated = await apiUpdateAudioTrack(id, body);
    setAudioTracks((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  }, []);

  const deleteAudioTrack = useCallback(async (id) => {
    await apiDeleteAudioTrack(id);
    setAudioTracks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ===== Template Usage (Recently Used) =====
  const recordTemplateUsage = useCallback(async (templateId) => {
    if (!user || !templateId) return;
    try {
      const res = await apiRecordTemplateUsage(templateId);
      setTemplateUsage((prev) => {
        const filtered = prev.filter((u) => u.template_id !== templateId);
        return [{ template_id: templateId, last_used_at: res.last_used_at }, ...filtered];
      });
    } catch (e) {
      // non-fatal
    }
  }, [user]);

  const recentlyUsedTemplateIds = templateUsage.map((u) => u.template_id);

  // ===== State persistence (auto-save with debounce, handled in caller) =====
  const persistState = useCallback(async (state) => {
    if (!user) return;
    try {
      await saveUserState(state);
    } catch (e) {
      // non-fatal
      console.warn('Failed to persist state:', e);
    }
  }, [user]);

  return (
    <UserDataContext.Provider
      value={{
        loading,
        // templates
        allTemplates,
        customTemplates,
        findTemplate,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        reorderTemplates,
        // backgrounds
        allBackgrounds,
        customBackgrounds,
        findBackground,
        uploadBackground,
        updateBackground,
        deleteBackground,
        resolveBackgroundUrl,
        defaultBackground,
        // audio
        audioTracks,
        uploadAudioTrack,
        updateAudioTrack,
        deleteAudioTrack,
        audioTrackUrl,
        // recently used
        recentlyUsedTemplateIds,
        recordTemplateUsage,
        // state
        savedState,
        persistState,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => {
  const ctx = useContext(UserDataContext);
  if (!ctx) throw new Error('useUserData must be used within UserDataProvider');
  return ctx;
};
